import { Component, Input } from '@angular/core';
import { ModalController, AlertController, IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IKeg } from 'app/models/keg';
import { ITapVM } from 'app/models/tap';
import * as fromTap from 'app/state/tap';

/**
 * Mirror of the tap-side keg-chooser (tap-detail/keg-chooser): lets you assign
 * the current keg to a tap from the keg detail page. Because the tap↔keg link
 * lives on the tap (ITap.kegId), assigning here writes kegId onto the chosen
 * tap. If that tap already holds a different keg, an override-confirm is shown —
 * the same UX as changing a keg on the tap detail page.
 */
@Component({
  selector: 'app-tap-chooser',
  templateUrl: './tap-chooser.page.html',
  styleUrls: ['./tap-chooser.page.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
})
export class TapChooserPage {

  @Input() keg!: IKeg;
  @Input() uid!: string;

  taps = this.store.selectSignal(fromTap.getItemsWithKegAndBeverage);

  constructor(
    private store: Store,
    private modalController: ModalController,
    private alertController: AlertController,
  ) { }

  async selectTap(tap: ITapVM) {
    if (tap.kegId && tap.kegId !== this.keg.id) {
      await this.confirmAlert(tap);
    } else {
      this.assign(tap);
    }
  }

  async confirmAlert(tap: ITapVM) {
    const alert = await this.alertController.create({
      header: 'Confirm!',
      message: 'This tap already has a keg, are you sure you want to change?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
        }, {
          text: 'Okay',
          handler: () => {
            this.assign(tap);
          },
        },
      ],
    });

    await alert.present();
  }

  private assign(tap: ITapVM) {
    // Move semantics: clear this keg from any other tap that currently holds it,
    // so a keg is never attached to two taps at once (mirrors the keg-chooser).
    for (const t of this.taps()) {
      if (t.id !== tap.id && t.kegId === this.keg.id) {
        this.store.dispatch(fromTap.softUpdate({ id: t.id ?? '', data: { kegId: '' }, uid: this.uid }));
      }
    }
    this.store.dispatch(fromTap.softUpdate({ id: tap.id ?? '', data: { kegId: this.keg.id }, uid: this.uid }));
    this.modalController.dismiss({ tap });
  }

  dismiss() {
    this.modalController.dismiss({
      dismissed: true,
    });
  }
}
